import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAnalyticsConfig } from './hooks/useAnalyticsConfig';
import { useAnalyticsInitialization } from './hooks/useAnalyticsInitialization';
import type { AnalyticsConfig } from './types/analytics.types';

// =================================================================================
// CONTEXT
// =================================================================================

const AnalyticsContext = createContext<AnalyticsConfig | null>(null);

// =================================================================================
// PROVIDER COMPONENT
// =================================================================================

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const config = useAnalyticsConfig();
  const { isInitialized, initializationError } = useAnalyticsInitialization(config);

  // Dispara evento customizado quando analytics estiver pronto
  useEffect(() => {
    if (isInitialized) {
      const event = new CustomEvent('analyticsInstallationDone', {
        detail: { config, timestamp: Date.now() }
      });
      window.dispatchEvent(event);
    }
  }, [isInitialized, config]);

  // Log de erro se houver problema na inicialização
  useEffect(() => {
    if (initializationError) {
      console.error('❌ Erro na inicialização do Analytics:', initializationError);
    }
  }, [initializationError]);

  return (
    <AnalyticsContext.Provider value={config}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// =================================================================================
// HOOKS
// =================================================================================

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalyticsContext deve ser usado dentro de AnalyticsProvider');
  }
  return context;
};

export const useClientConfig = () => {
  const context = useContext(AnalyticsContext);
  return context || {
    gtm_ids: [],
    ga4_ids: [],
    ads_ids: [],
    facebook_pixels: [],
    tiktok_pixels: [],
    pinterest_tags: [],
    client_slug: '',
    storeId: '',
    nanoId: ''
  };
}; 